import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import {
  User,
  Shield,
  Bell,
  CreditCard,
  Settings,
  Upload,
  Plus,
  Menu,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
// AppFrame removed: route-level layout wraps pages
import { useAuth } from "wasp/client/auth";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getUserProfile,
  updateUserProfile,
  updateEmail,
} from "wasp/client/operations";
import { toast } from "sonner";
import { SubscriptionSection } from "../components/account/SubscriptionSection";
import { DISPLAY_NAME, normalizeTier } from "../../billing/tiers";
import { computeUsage, type Tier } from "../../lib/planLimits";
import { AnimatedSkeleton } from "../components/ui/animated-skeleton";
import {
  getUsageStatus,
  getBillingSummary,
  createStripePortalSession,
  createLemonSqueezyPortalSession,
} from "wasp/client/operations";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

const sidebarItems = [
  { id: "profile", label: "Profile", icon: User },
  { id: "security", label: "Security", icon: Shield },
  { id: "notifications", label: "Notification", icon: Bell },
  // Commented out integrations as not needed
  // { id: "integrations", label: "Integration", icon: Settings }
];

export function SettingsPage() {
  const { data: user } = useAuth();
  const { data: userProfile } = useQuery(
    getUserProfile,
    {},
    { enabled: !!user },
  );
  const doUpdateProfile = useAction(updateUserProfile);
  const doUpdateEmail = useAction(updateEmail);
  const location = useLocation();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState("profile");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync active section with query param (?section=billing|profile|security|notifications)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const section = params.get("section");
      const allowed = new Set(sidebarItems.map((s) => s.id));
      if (section && allowed.has(section)) {
        setActiveSection(section);
      }
    } catch {}
  }, [location.search]);

  const getUserDisplayName = () => {
    if (user?.displayName) return user.displayName;
    if (user?.firstName) return user.firstName;
    if (user?.email) return String(user.email).split("@")[0];
    return "Chef";
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleUpdateProfile = async (data: any) => {
    setIsLoading(true);
    try {
      await doUpdateProfile(data);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmail = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await doUpdateEmail({ email, password });
      toast.success("Email updated successfully");
    } catch (error) {
      toast.error("Failed to update email");
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return (
          <ProfileSection
            onUpdate={handleUpdateProfile}
            user={user}
            userProfile={userProfile}
          />
        );
      case "security":
        return <SecuritySection onUpdateEmail={handleUpdateEmail} />;
      case "notifications":
        return (
          <NotificationsSection
            onUpdate={handleUpdateProfile}
            userProfile={userProfile}
          />
        );
      case "billing":
        return <BillingSection />;
      // Commented out integrations as not needed
      // case "integrations":
      //   return <IntegrationsSection />;
      default:
        return (
          <ProfileSection
            onUpdate={handleUpdateProfile}
            user={user}
            userProfile={userProfile}
          />
        );
    }
  };

  return (
    <div>
      {/* Mobile menu button */}
      <div className="mb-4 lg:hidden">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      <div className="lg:flex lg:gap-6">
        {/* Sidebar */}
        <div className="hidden w-64 shrink-0 border-r bg-card p-6 lg:block">
          <nav className="space-y-2">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    activeSection === item.id
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Mobile slide-over using Sheet (focus trap + ESC + swipe) */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="w-64 p-6 sm:w-80">
            <SheetHeader>
              <SheetTitle>Settings</SheetTitle>
            </SheetHeader>
            <nav className="mt-4 space-y-2">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setSidebarOpen(false);
                    }}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      activeSection === item.id
                        ? "bg-secondary text-secondary-foreground"
                        : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </SheetContent>
        </Sheet>

        {/* Main content */}
        <div className="flex-1">
          <div className="space-y-6">
            <div className="">{renderContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileSection({ onUpdate, user, userProfile }: any) {
  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    displayName: user?.displayName || "",
    email: user?.email || "",
    // Commented out address and phone as not needed
    // phone: '',
    // address: '',
    // city: '',
    // postalCode: '',
    // country: 'us'
  });

  const handleSubmit = (e: any) => {
    e.preventDefault();
    onUpdate(formData);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-balance text-2xl font-semibold">
          Personal information
        </h1>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src="" />
                <AvatarFallback>
                  {user?.email
                    ? String(user.email).split("@")[0].slice(0, 2).toUpperCase()
                    : "CH"}
                </AvatarFallback>
              </Avatar>
              <div className="flex gap-2">
                <Button size="sm">
                  <Upload />
                  Upload image
                </Button>
                <Button variant="outline" size="sm">
                  Remove
                </Button>
              </div>
            </div>

            {/* Personal info form */}
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 gap-4 md:grid-cols-2"
            >
              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Display name</Label>
                <Input
                  id="displayName"
                  value={formData.displayName}
                  onChange={(e) =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              {/* Commented out phone and address as not needed */}
              {/* <div className="space-y-2">
                <Label htmlFor="phone">Phone number</Label>
                <div className="flex gap-2">
                  <Select defaultValue="us">
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us">🇺🇸 +1</SelectItem>
                      <SelectItem value="uk">🇬🇧 +44</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input className="flex-1" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </div>
              </div> */}
            </form>

            {/* Commented out address section as not needed */}
            {/* <Card>
              <CardHeader>
                <CardTitle>Address information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select defaultValue="us">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us">🇺🇸 United States</SelectItem>
                      <SelectItem value="uk">🇬🇧 United Kingdom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postalCode">Postal Code</Label>
                    <Input id="postalCode" value={formData.postalCode} onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })} />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button>Save</Button>
                </div>
              </CardContent>
            </Card> */}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SecuritySection({ onUpdateEmail }: any) {
  const [emailData, setEmailData] = useState({
    email: "",
    password: "",
  });

  const handleUpdateEmail = (e: any) => {
    e.preventDefault();
    onUpdateEmail(emailData.email, emailData.password);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-balance text-2xl font-semibold">Security</h1>
        <p className="mt-1 text-muted-foreground">
          Manage your account security and authentication.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="space-y-2">
            <Label htmlFor="currentEmail">Current email</Label>
            <Input
              id="currentEmail"
              defaultValue="john.doe@example.com"
              disabled
            />
          </div>

          <form onSubmit={handleUpdateEmail} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newEmail">New email</Label>
              <Input
                id="newEmail"
                type="email"
                value={emailData.email}
                onChange={(e) =>
                  setEmailData({ ...emailData, email: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={emailData.password}
                onChange={(e) =>
                  setEmailData({ ...emailData, password: e.target.value })
                }
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit">Update Email</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2-Step verification</CardTitle>
          <CardDescription>
            Add an extra layer of security to your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Commented out 2FA options except email verification */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100">
                <span className="text-sm font-semibold text-orange-600">@</span>
              </div>
              <div>
                <h4 className="font-medium">Email verification</h4>
                <p className="text-sm text-muted-foreground">
                  Unique codes sent to email for confirming logins.
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm">
              Enable
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function NotificationsSection({ onUpdate, userProfile }: any) {
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyDigest: userProfile?.weeklyDigestOptIn || true,
    mealReminders: true,
    billingAlerts: true,
    newsUpdates: false,
    tipsTutorials: false,
    offersPromotions: false,
    followUpReminders: false,
  });

  const handleUpdateNotifications = (e: any) => {
    e.preventDefault();
    onUpdate({ weeklyDigestOptIn: notificationSettings.weeklyDigest });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-balance text-2xl font-semibold">Notification</h1>
      </div>

      <Card>
        <CardContent className="space-y-6 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Enable desktop notification</h3>
              <p className="text-sm text-muted-foreground">
                Decide whether you want to be notified of new message & updates
              </p>
            </div>
            <Switch
              checked={notificationSettings.pushNotifications}
              onCheckedChange={(checked) =>
                setNotificationSettings({
                  ...notificationSettings,
                  pushNotifications: checked,
                })
              }
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Enable unread notification badge</h3>
              <p className="text-sm text-muted-foreground">
                Display a red indicator on of the notification icon when you
                have unread message
              </p>
            </div>
            <Switch />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Email notification</h3>
              <p className="text-sm text-muted-foreground">
                Receive notifications via email
              </p>
            </div>
            <Switch
              checked={notificationSettings.emailNotifications}
              onCheckedChange={(checked) =>
                setNotificationSettings({
                  ...notificationSettings,
                  emailNotifications: checked,
                })
              }
            />
          </div>

          <div className="space-y-4">
            <h3 className="font-medium">Email preferences</h3>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="weeklyDigest"
                checked={notificationSettings.weeklyDigest}
                onCheckedChange={(checked) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    weeklyDigest: checked,
                  })
                }
              />
              <div>
                <Label htmlFor="weeklyDigest" className="font-medium">
                  Weekly Summary
                </Label>
                <p className="text-sm text-muted-foreground">
                  Get a weekly summary of your activity
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="mealReminders"
                checked={notificationSettings.mealReminders}
                onCheckedChange={(checked) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    mealReminders: Boolean(checked),
                  })
                }
              />
              <div>
                <Label htmlFor="mealReminders" className="font-medium">
                  Meal Reminders
                </Label>
                <p className="text-sm text-muted-foreground">
                  Reminders for meal planning and cooking
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="billingAlerts"
                checked={notificationSettings.billingAlerts}
                onCheckedChange={(checked) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    billingAlerts: Boolean(checked),
                  })
                }
              />
              <div>
                <Label htmlFor="billingAlerts" className="font-medium">
                  Billing Alerts
                </Label>
                <p className="text-sm text-muted-foreground">
                  Notifications about billing and payments
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="newsUpdates"
                checked={notificationSettings.newsUpdates}
                onCheckedChange={(checked) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    newsUpdates: Boolean(checked),
                  })
                }
              />
              <div>
                <Label htmlFor="newsUpdates" className="font-medium">
                  News & updates
                </Label>
                <p className="text-sm text-muted-foreground">
                  News about product and features update
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="tipsTutorials"
                checked={notificationSettings.tipsTutorials}
                onCheckedChange={(checked) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    tipsTutorials: Boolean(checked),
                  })
                }
              />
              <div>
                <Label htmlFor="tipsTutorials" className="font-medium">
                  Tips & tutorials
                </Label>
                <p className="text-sm text-muted-foreground">
                  Tips & trick in order to increase your performance efficiency
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="offersPromotions"
                checked={notificationSettings.offersPromotions}
                onCheckedChange={(checked) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    offersPromotions: Boolean(checked),
                  })
                }
              />
              <div>
                <Label htmlFor="offersPromotions" className="font-medium">
                  Offer & promotions
                </Label>
                <p className="text-sm text-muted-foreground">
                  Promotion about product price & latest discount
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="followUpReminders"
                checked={notificationSettings.followUpReminders}
                onCheckedChange={(checked) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    followUpReminders: Boolean(checked),
                  })
                }
              />
              <div>
                <Label htmlFor="followUpReminders" className="font-medium">
                  Follow up reminder
                </Label>
                <p className="text-sm text-muted-foreground">
                  Receive notification all the reminder that have been made
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleUpdateNotifications}>
              Save Preferences
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BillingSection() {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const isAuthenticated = !!user;

  const { data: billingSummary, isLoading: billingLoading } = useQuery(
    getBillingSummary,
    {},
    { enabled: isAuthenticated },
  );
  const { data: usageStatus, isLoading: usageLoading } = useQuery(
    getUsageStatus,
    {},
    { enabled: isAuthenticated },
  );
  const doStripePortal = useAction(createStripePortalSession);
  const doLqsPortal = useAction(createLemonSqueezyPortalSession);

  const handleUpgrade = () => {
    navigate("/pricing");
  };

  const handleManageBilling = async () => {
    if (billingLoading) return;
    const summary = billingSummary as any;
    if (!summary?.currentPlan?.provider) {
      toast.error("No active subscription found.");
      return;
    }
    try {
      if (summary.currentPlan.provider === "stripe") {
        const res: any = await doStripePortal({} as any);
        if (res?.url) window.open(res.url as string, "_blank", "noopener");
        else toast.error("No portal URL returned");
      } else if (summary.currentPlan.provider === "lemonsqueezy") {
        const res: any = await doLqsPortal({} as any);
        if (res?.url) window.open(res.url as string, "_blank", "noopener");
        else
          toast.info("Contact support to manage Lemon Squeezy subscription.");
      }
    } catch (e) {
      toast.error("Unable to open billing portal.");
    }
  };

  const handleDownloadInvoice = (invoiceId: string) => {
    // Redirect users to provider portal for now
    handleManageBilling();
  };

  if (usageLoading || billingLoading) {
    return (
      <div className="space-y-6">
        <AnimatedSkeleton variant="shimmer" className="h-8 w-64 rounded-md" />
        <AnimatedSkeleton variant="shimmer" className="h-64 rounded-lg" />
        <AnimatedSkeleton variant="wave" className="h-48 rounded-lg" />
      </div>
    );
  }

  // Derive subscription info for UI from billing summary
  const tierRaw =
    (billingSummary as any)?.currentPlan?.tier ||
    (user as any)?.subscriptionTier ||
    "starter";
  const tier = normalizeTier(tierRaw);

  const summary = billingSummary as any;
  const currentPlanStatus = summary?.currentPlan?.status || "expired";
  const normalizedStatus: "active" | "trialing" | "expired" =
    currentPlanStatus === "trialing"
      ? "trialing"
      : currentPlanStatus === "active"
        ? "active"
        : "expired";

  const subscriptionInfo = {
    tier,
    planName: `${DISPLAY_NAME[tier]} Plan`,
    price: Number(summary?.currentPlan?.priceUsd || 0),
    billingPeriod: "monthly" as "monthly" | "yearly",
    status: normalizedStatus,
    nextBillingDate: summary?.currentPlan?.nextBillingDate
      ? new Date(summary.currentPlan.nextBillingDate).toLocaleDateString()
      : undefined,
    features: [
      { name: "Meal plan generation", included: true },
      { name: "Shopping list", included: true },
      { name: "Recipe swaps", included: true },
      { name: "AI personalization", included: tier === "core_plus" },
    ],
  };

  // Usage
  const userTier: Tier = (user as any)?.subscriptionTier
    ? normalizeTier((user as any).subscriptionTier)
    : "starter";
  const planUsage = computeUsage(
    userTier,
    (usageStatus as any)?.plansUsed ?? 0,
  );
  const swapUsage = computeUsage(
    userTier,
    (usageStatus as any)?.swapsUsed ?? 0,
  );
  const usageData = {
    mealPlans: { current: planUsage.used, limit: planUsage.limit },
    recipeSwaps: { current: swapUsage.used, limit: swapUsage.limit },
    aiGenerations: { current: 0, limit: 50 },
  };

  // Billing history mapping
  const billingHistory = ((billingSummary as any)?.paymentHistory || []).map(
    (p: any, idx: number) => ({
      id: p.invoice || `txn-${idx}`,
      date: new Date(p.date).toLocaleDateString(),
      amount: `$${Number(p.amount).toFixed(2)}`,
      status: (p.status === "succeeded"
        ? "paid"
        : p.status === "pending"
          ? "pending"
          : "failed") as "paid" | "pending" | "failed",
      plan: DISPLAY_NAME[tier],
      invoice: p.invoice || undefined,
    }),
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-balance text-2xl font-semibold">Billing</h1>
        <p className="mt-1 text-muted-foreground">
          Manage your subscription, usage, and invoices.
        </p>
      </div>

      <SubscriptionSection
        subscription={subscriptionInfo}
        usage={usageData}
        billingHistory={billingHistory}
        onUpgrade={handleUpgrade}
        onManageBilling={handleManageBilling}
        onDownloadInvoice={handleDownloadInvoice}
      />
    </div>
  );
}

// Commented out integrations as not needed
// function IntegrationsSection() {
//   return (
//     <div className="space-y-6">
//       <div>
//         <h1 className="text-2xl font-semibold text-balance">Integration</h1>
//         <p className="text-muted-foreground mt-1">
//           Supercharge your workflow using these integration
//         </p>
//       </div>

//       <div className="space-y-4">
//         {integrations.map((integration) => (
//           <Card key={integration.name}>
//             <CardContent className="p-6">
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center gap-3">
//                   <div className="text-2xl">{integration.icon}</div>
//                   <div>
//                     <h3 className="font-semibold">{integration.name}</h3>
//                     <p className="text-muted-foreground text-sm">{integration.description}</p>
//                   </div>
//                 </div>
//                 <div className="flex items-center gap-3">
//                   <Button variant="ghost" size="sm" className="text-muted-foreground">
//                     Learn more
//                   </Button>
//                   <Switch defaultChecked={integration.enabled} />
//                 </div>
//               </div>
//             </CardContent>
//           </Card>
//         ))}
//       </div>
//     </div>
//   );
// }
